import { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  FormErrorMessage,
  VStack,
  Divider,
  Flex,
  Heading,
  Icon,
  useToast,
  ChakraProvider,
  theme,
  Box,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik, FormikHelpers } from "formik";
import { MdAccountBalance } from "react-icons/md";
import { getBanks } from "@/services/bank.service";
import InputMask from "react-input-mask";
import { useParams } from "react-router-dom";
import { bankDetailsValidationSchemaConsignado } from "./BankDetailModalValidation";
import { AccountTypeLabelsConsignado, BankDetailsConsignado, IConsignadoProposalDetail } from "@/models/consignado/proposal.model";
import { getConsignadoProposalById, updateProposalBankDetails } from "@/services/consignado/proposal.service";
import { mapProposalBankDetails } from "@/services/consignado/mapper/proposalMapper";

interface BankDetailUpdateFormProps {
  isOpen: boolean;
  onClose: () => void;
  proposalId?: string
}

export default function BankDetailModalConsignado({
  isOpen,
  onClose,
  proposalId,
}: BankDetailUpdateFormProps) {
  const { id: paramId } = useParams<{ id: string }>();
  const finalId = proposalId ?? paramId ?? null;
  const [banks, setBanks] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const [ proposal, setProposal ] = useState<IConsignadoProposalDetail | null>();

  useEffect(() => {
    if (!finalId) return;
    
    const fetchBanks = async () => {
      try {
        const response = await getBanks(1, 22);
        setBanks(
          response.data.map((bank: any) => ({ id: bank.id, name: bank.name }))
        );
      } finally {
        setIsLoading(false);
      }
    };
    const fetchProposal = async () => {
      try {
        setIsLoading(true);
        const data = await getConsignadoProposalById(finalId!);
        setProposal(data);
      } catch (error) {
        console.error("Erro ao buscar proposta:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchProposal()
      fetchBanks();
    }
  }, [isOpen]);

  const initialValues = {
    account: proposal?.conta || "",
    accountDigit: proposal?.digito_conta || "",
    agency: proposal?.agencia ||  "",
    agencyDigit: proposal?.digito_agencia || "",
    bankCode: proposal?.numero_banco || "",
    accountType: parseInt(proposal?.tipo_conta!) || 1,
  };

  const handleSubmit = async (
    values: BankDetailsConsignado,
    actions: FormikHelpers<BankDetailsConsignado>
  ) => {
    try {
      const bankDetailsMapped = mapProposalBankDetails(values)

      await updateProposalBankDetails(proposal?.id_proposta!, bankDetailsMapped)

      toast({
        title: "Dados Bancários Corrigidos",
        description: "Os Dados Bancários foram corrigidos com sucesso",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch {
      toast({
        title: "Erro",
        description: "Houve um erro ao corrigir dados bancários. Por favor, tente novamente em instantes.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
         { isLoading || !proposal?   (
            <ChakraProvider theme={theme}>
              <Box
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                padding="3em"
              >
                <VStack spacing={4}>
                  <Spinner size="xl" color="primary" thickness="4px" speed="0.65s" />
                  <Text fontSize="xl" fontWeight="medium">
                    Carregando...
                  </Text>
                </VStack>
              </Box>
            </ChakraProvider>) : (
            <>
        <ModalHeader>
          <Flex align="center" justify="space-between" width="100%">
            <Flex align="center">
              <Icon as={MdAccountBalance} boxSize={8} color="gray.500" mr={2} />
              <Heading size="md" color="gray.700" fontFamily="Lexend Deca">
                Dados Bancários do seu cliente
              </Heading>
            </Flex>
            <ModalCloseButton position="static" />
          </Flex>
        </ModalHeader>
        <Divider mb={6} />
        <Formik
          initialValues={initialValues}
          validationSchema={bankDetailsValidationSchemaConsignado}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form>
              <ModalBody>
                <VStack spacing={4}>
                  <Field name="bankCode">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          !!(form.errors.bankCode && form.touched.bankCode)
                        }
                      >
                        <FormLabel htmlFor="bankCode">Banco</FormLabel>
                        <Select
                          {...field}
                          id="bankCode"
                          placeholder="Selecione o banco"
                          variant="flushed"
                          isDisabled={isLoading}
                        >
                          {banks.map((bank) => (
                            <option key={bank.id} value={bank.id}>
                              {bank.name}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>
                          {form.errors.bankCode as string}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="accountType">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          !!(
                            form.errors.accountType &&
                            form.touched.accountType
                          )
                        }
                      >
                        <FormLabel htmlFor="accountType">
                          Tipo de Conta
                        </FormLabel>
                        <Select
                          {...field}
                          id="accountType"
                          variant="flushed"
                          placeholder="Selecione o tipo de conta"
                        >
                          {Object.entries(AccountTypeLabelsConsignado).map(
                            ([key, label]) => (
                              <option key={key} value={key}>
                                {label}
                              </option>
                            )
                          )}
                        </Select>
                        <FormErrorMessage>
                          {form.errors.accountType as string}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="agency">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          !!(form.errors.agency && form.touched.agency)
                        }
                      >
                        <FormLabel htmlFor="agency">Agência</FormLabel>
                        <Input
                          as={InputMask}
                          mask="9999"
                          maskChar=""
                          {...field}
                          id="agency"
                          placeholder="Digite a agência"
                          variant="flushed"
                        />
                        <FormErrorMessage>
                          {form.errors.agency as string}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="agencyDigit">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          !!(form.errors.agencyDigit && form.touched.agencyDigit)
                        }
                      >
                        <FormLabel htmlFor="agency">Digito da Agência</FormLabel>
                        <Input
                          as={InputMask}
                          mask="9"
                          maskChar=""
                          {...field}
                          id="agency"
                          placeholder="Digite o digito aa agência"
                          variant="flushed"
                        />
                        <FormErrorMessage>
                          {form.errors.agencyDigit as string}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>


                  <Field name="account">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          !!(form.errors.account && form.touched.account)
                        }
                      >
                        <FormLabel htmlFor="account">
                          Número da Conta
                        </FormLabel>
                        <Input
                          {...field}
                          as={InputMask}
                          mask="999999999"
                          maskChar=""
                          id="account"
                          placeholder="Digite o número da conta"
                          variant="flushed"
                        />
                        <FormErrorMessage>
                          {form.errors.account as string}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="accountDigit">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          !!(
                            form.errors.accountDigit &&
                            form.touched.accountDigit
                          )
                        }
                      >
                        <FormLabel htmlFor="accountDigit">Dígito</FormLabel>
                        <Input
                          {...field}
                          as={InputMask}
                          mask="9"
                          maskChar=""
                          id="accountDigit"
                          placeholder="Digite o dígito da conta"
                          variant="flushed"
                        />
                        <FormErrorMessage>
                          {form.errors.accountDigit as string}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
  
                </VStack>
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="primary"
                  mr={3}
                  isDisabled={props.isSubmitting}
                  type="submit"
                >
                  {props.isSubmitting ? 'Corrigindo...' : 'Corrigir'}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
           </>
         )}
      
      </ModalContent>
    </Modal>
  );
}
